import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component } from "preact";
/** @import { ComponentChildren } from "preact"; */

import html from "~/util/html.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Field.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Field.module.css", import.meta.url);
const css = adopt(sheet);

/** @typedef {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} ValidatableElement */

/**
 * @typedef {object} Props
 * @property {string} label The label text.
 * @property {string} for The ID of the corresponding input.
 * @property {ComponentChildren} [info]
 * @property {string | undefined} [error] An error message to be displayed below the input.
 * @property {{ [K in keyof ValidityState]?: string | false }} [validity] Messages for each of the
 *   input validity states (https://developer.mozilla.org/en-US/docs/Web/API/ValidityState)
 * @property {ComponentChildren} [children]
 */

/** @extends {Component<Props>} */
export default class Field extends Component {
  invalid = signal("");

  /** @type {(ref: HTMLLabelElement) => () => void} */
  label = ref => {
    const ctrl = new AbortController();
    const el = /** @type {HTMLInputElement | HTMLTextAreaElement | null} */ref.control;
    el?.addEventListener("change", ev => this.#onChange(ev), {
      signal: ctrl.signal
    });
    el?.addEventListener("invalid", ev => this.#onInvalid(ev), {
      signal: ctrl.signal
    });
    el?.form?.addEventListener("reset", ev => this.#onReset(ev), {
      signal: ctrl.signal
    });
    return () => ctrl.abort();
  };

  /** @param {Event} ev */
  #onChange(ev) {
    const el = /** @type {ValidatableElement} */ev.currentTarget;

    // reset any custom message before checking for validity again
    el.setCustomValidity("");

    // if the input is valid, clear the error message
    if (el.reportValidity()) this.invalid.value = "";
  }

  /** @param {Event} ev */
  #onInvalid(ev) {
    const el = /** @type {ValidatableElement} */ev.currentTarget;

    // if the element is valid, no need to process further
    if (el.validity.valid) return;

    // prevent the default invalid message behavior (e.g. showing a popover)
    ev.preventDefault();

    // find a custom message for the first failing validity state
    let custom = "";
    for (const [key, message] of Object.entries(this.props.validity ?? {})) {
      if (message && el.validity[(/** @type {keyof ValidityState} */key)]) {
        custom = message;
        break;
      }
    }

    // set a custom validity message (if any)
    el.setCustomValidity(custom);

    // set the invalid message to the element's validity message (whether custom or default)
    if (el.validationMessage) this.invalid.value = el.validationMessage;
  }

  /** @param {Event} _ev */
  #onReset(_ev) {
    this.invalid.value = "";
  }
  render() {
    const {
      label,
      info,
      error,
      children
    } = this.props;
    const message = this.invalid.value || error;
    return __h("div", {
      "class": css.wrapper
    }, __h("label", {
      "class": css.label,
      "for": this.props.for,
      ref: this.label
    }, label), __h("div", {
      "class": css.contents
    }, children), info ? __h("div", {
      "class": css.info
    }, info) : null, __h("p", {
      "class": css.error,
      role: "status"
    }, message));
  }
}